import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Check,
  ChevronDown,
  Server,
  ArrowRight,
  Loader2,
  CheckCircle,
  XCircle,
  Users,
  Play,
  RotateCw,
  Mail,
  Lock,
  Eye,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Shield,
  Info,
  Database,
  Plus
} from 'lucide-react'
import { 
  inputAccountsManual, 
  configureServers,
  testConnections,
  setPasswordOptions,
  createDestinationEmails,
  startMigration,
  getProgress,
  getAllServers,
  testServerConnection,
  executeCommand,
  getEmailBatches,
  getEmailBatch
} from '@/services/api'
import { Terminal } from '@/components/ui/terminal'
import { EmailCreationWizard } from '@/components/EmailCreationWizard'
import { PasswordUpdateWizard } from '@/components/PasswordUpdateWizard'
import { MigrationTerminal } from '@/components/MigrationTerminal'

const STEPS = [
  { id: 'accounts', label: 'Upload Accounts', description: 'Add email accounts to migrate' },
  { id: 'servers', label: 'Servers & Options', description: 'Choose servers, verify access, and set password options' },
  { id: 'create', label: 'Create Emails', description: 'Create accounts on destination' },
  { id: 'migrate', label: 'Migrate & Resync', description: 'Transfer email and resync new mail' },
]

const STEP_MAP = {
  'accounts': 0,
  'servers': 1,
  // 'passwords' folded into the servers step; keep the alias so old links resolve
  'passwords': 1,
  'create': 2,
  'migrate': 3,
}

export default function MigrationWizard() {
  const navigate = useNavigate()
  const { jobId, action } = useParams()
  const isEditMode = !!jobId
  
  const [currentStep, setCurrentStep] = useState(STEP_MAP[action] || 0)
  const [servers, setServers] = useState([])
  const [loading, setLoading] = useState(false)
  
  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  })
  
  // Helper to show alert modal
  const showAlert = (title, message, type = 'error') => {
    setAlertModal({ isOpen: true, title, message, type })
  }
  
  // Step 1: Accounts
  const [jobName, setJobName] = useState('')

  const [manualInput, setManualInput] = useState('')
  const [inputMethod, setInputMethod] = useState('batch')
  const [createdJobId, setCreatedJobId] = useState(jobId)
  const [accounts, setAccounts] = useState([])
  
  // Email batches
  const [emailBatches, setEmailBatches] = useState([])
  const [selectedBatchId, setSelectedBatchId] = useState('')
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null)
  const [batchOpen, setBatchOpen] = useState(false)
  
  // Step 2: Servers
  const [sourceServerId, setSourceServerId] = useState('')
  const [destServerId, setDestServerId] = useState('')
  const [sourceOpen, setSourceOpen] = useState(false)
  const [destOpen, setDestOpen] = useState(false)
  
  // Step 2: Server Selection & Connection Test (combined)
  const [connectionStatus, setConnectionStatus] = useState({
    source: { testing: false, success: null, message: '', responseTime: 0, hasRootAccess: null, hasShellAccess: null, serverType: null },
    destination: { testing: false, success: null, message: '', responseTime: 0, hasRootAccess: null, hasShellAccess: null, serverType: null },
  })
  
  // Step 4: Password Options
  const [updateSourcePasswords, setUpdateSourcePasswords] = useState(false)
  const [storePasswords, setStorePasswords] = useState(true)
  const [passwordUpdateStarted, setPasswordUpdateStarted] = useState(false)
  const [passwordUpdateComplete, setPasswordUpdateComplete] = useState(false)
  
  // Step 5: Create Emails
  const [emailCreationStatus, setEmailCreationStatus] = useState([])
  const [creatingEmails, setCreatingEmails] = useState(false)
  const [emailCreationStarted, setEmailCreationStarted] = useState(false)
  
  // Step 6: Migration
  const [migrationStatus, setMigrationStatus] = useState(null)
  const [migrating, setMigrating] = useState(false)

  // Persisted per-account progress (seeds the Create + Migrate steps on revisit)
  const [accountProgress, setAccountProgress] = useState([])

  // Seed statuses for the Create Emails step: persisted creation state, with any
  // in-session results layered on top.
  const creationInitialStatuses = useMemo(() => {
    const m = {}
    accountProgress.forEach(a => {
      m[a.email] = {
        status: a.emailCreated ? 'created' : 'pending',
        message: a.emailCreationMessage || (a.emailCreated ? 'Already created' : undefined),
      }
    })
    emailCreationStatus.forEach(s => {
      if (s?.email) m[s.email] = { status: s.status, message: s.message }
    })
    return m
  }, [accountProgress, emailCreationStatus])

  // Load saved state from localStorage on mount
  useEffect(() => {
    loadServers()
    loadEmailBatches()
    if (jobId) {
      loadJobData()
      loadSavedState()
    }
  }, [jobId])

  // Save state to localStorage whenever key data changes
  useEffect(() => {
    if (createdJobId) {
      saveState()
    }
  }, [createdJobId, currentStep, sourceServerId, destServerId, connectionStatus, updateSourcePasswords, storePasswords, emailCreationStatus])

  const getStorageKey = () => `migration_wizard_${createdJobId || 'new'}`

  const saveState = () => {
    const state = {
      currentStep,
      sourceServerId,
      destServerId,
      connectionStatus,
      updateSourcePasswords,
      storePasswords,
      emailCreationStatus,
      emailCreationStarted,
      jobName,
      timestamp: Date.now()
    }
    localStorage.setItem(getStorageKey(), JSON.stringify(state))
  }

  const loadSavedState = () => {
    try {
      const saved = localStorage.getItem(getStorageKey())
      if (saved) {
        const state = JSON.parse(saved)
        // Only restore if less than 24 hours old
        if (Date.now() - state.timestamp < 24 * 60 * 60 * 1000) {
          if (state.sourceServerId) setSourceServerId(state.sourceServerId)
          if (state.destServerId) setDestServerId(state.destServerId)
          if (state.connectionStatus) setConnectionStatus(state.connectionStatus)
          if (state.updateSourcePasswords !== undefined) setUpdateSourcePasswords(state.updateSourcePasswords)
          if (state.storePasswords !== undefined) setStorePasswords(state.storePasswords)
          if (state.emailCreationStatus) setEmailCreationStatus(state.emailCreationStatus)
          if (state.emailCreationStarted) setEmailCreationStarted(state.emailCreationStarted)
          if (state.jobName) setJobName(state.jobName)
          // Restore step if valid. Map legacy 5-step indices onto the new 4-step
          // model where Password Options is folded into the Servers step.
          // Legacy: 0=accounts 1=servers+test 2=passwords 3=create 4=migrate
          // New:    0=accounts 1=servers+options       2=create 3=migrate
          if (state.currentStep !== undefined) {
            let restoredStep = state.currentStep
            if (restoredStep === 2) restoredStep = 1        // passwords -> servers
            else if (restoredStep >= 3) restoredStep = restoredStep - 1
            restoredStep = Math.min(Math.max(restoredStep, 0), 3)
            if (restoredStep > currentStep) setCurrentStep(restoredStep)
          }
        }
      }
    } catch (err) {
      console.error('Error loading saved state:', err)
    }
  }

  const clearSavedState = () => {
    localStorage.removeItem(getStorageKey())
  }

  const loadServers = async () => {
    try {
      const data = await getAllServers()
      setServers(data)
    } catch (err) {
      console.error('Error loading servers:', err)
    }
  }
  
  const loadEmailBatches = async () => {
    try {
      const data = await getEmailBatches()
      setEmailBatches(data)
    } catch (err) {
      console.error('Error loading email batches:', err)
    }
  }

  const loadJobData = async () => {
    setLoading(true)
    try {
      const progress = await getProgress(jobId)
      setJobName(progress.jobName || '')
      setAccounts(progress.accounts || [])
      setAccountProgress(progress.accounts || [])
      // Reflect any previously-created emails so revisiting the Create step
      // shows them instead of resetting to "pending".
      if (progress.accounts?.length) {
        setEmailCreationStatus(progress.accounts.map(a => ({
          email: a.email,
          status: a.emailCreated ? 'created' : 'pending',
          message: a.emailCreationMessage || (a.emailCreated ? 'Already created' : 'Waiting to start...'),
        })))
      }
      if (progress.config) {
        setSourceServerId(progress.config.sourceServerId || '')
        setDestServerId(progress.config.destinationServerId || '')
        setUpdateSourcePasswords(progress.config.updateSourcePasswords || false)
        setStorePasswords(progress.config.storePasswords !== false)
      }
    } catch (err) {
      console.error('Error loading job:', err)
    }
    setLoading(false)
  }

  // Step 1: Create Job with Accounts
  const handleCreateJob = async () => {
    if (inputMethod === 'manual' && !manualInput.trim()) return
    if (inputMethod === 'batch' && !selectedBatchId) return
    
    // If job already exists, just move to next step instead of creating duplicate
    if (createdJobId) {
      setCurrentStep(1)
      navigate(`/dashboard/migrations/${createdJobId}/servers`, { replace: true })
      return
    }
    
    setLoading(true)
    try {
      let response
      if (inputMethod === 'batch') {
        // Load batch details and create accounts (only selected items)
        const batch = await getEmailBatch(selectedBatchId)
        const selectedItems = batch.items.filter(item => item.selected !== false)
        if (selectedItems.length === 0) {
          showAlert('Error', 'No emails selected in batch. Please select at least one email.', 'error')
          setLoading(false)
          return
        }
        const accountsText = selectedItems.map(item => 
          `${item.email}:${item.password}:${item.quotaMb || 1024}`
        ).join('\n')
        response = await inputAccountsManual(accountsText, jobName)
      } else {
        response = await inputAccountsManual(manualInput, jobName)
      }
      
      if (response.success) {
        setCreatedJobId(response.jobId)
        setAccounts(response.accounts || [])
        setCurrentStep(1)
        // Update URL with jobId so state persists on refresh
        navigate(`/dashboard/migrations/${response.jobId}/servers`, { replace: true })
        // Save initial state
        setTimeout(saveState, 0)
      }
    } catch (err) {
      console.error('Error creating job:', err)
      showAlert('Error', 'Error creating job: ' + err.message, 'error')
    }
    setLoading(false)
  }

  // Step 2: Configure Servers and Test Connection (combined)
  const handleConfigureServers = async () => {
    if (!sourceServerId || !destServerId) return
    
    setLoading(true)
    try {
      await configureServers(createdJobId, sourceServerId, destServerId)
      // Auto-test connections after server selection
      await testServerConnections()
      setTimeout(saveState, 0)
    } catch (err) {
      console.error('Error configuring servers:', err)
      showAlert('Error', 'Error configuring servers: ' + err.message, 'error')
    }
    setLoading(false)
  }

  // Test Connections - gets access level info from backend
  const testServerConnections = async () => {
    const sourceServer = getSelectedServer(sourceServerId)
    const destServer = getSelectedServer(destServerId)
    
    setConnectionStatus(prev => ({
      source: { ...prev.source, testing: true, success: null, hasRootAccess: null, hasShellAccess: null, serverType: sourceServer?.type },
      destination: { ...prev.destination, testing: true, success: null, hasRootAccess: null, hasShellAccess: null, serverType: destServer?.type },
    }))
    
    // Test source server
    try {
      const sourceResult = await testServerConnection(sourceServerId)
      setConnectionStatus(prev => ({
        ...prev,
        source: {
          testing: false,
          success: sourceResult.success,
          message: sourceResult.message,
          responseTime: sourceResult.responseTimeMs,
          hasRootAccess: sourceResult.hasRootAccess,
          hasShellAccess: sourceResult.hasShellAccess,
          serverType: sourceServer?.type,
        },
      }))
    } catch (err) {
      setConnectionStatus(prev => ({
        ...prev,
        source: {
          testing: false,
          success: false,
          message: 'Test failed - you can still continue',
          responseTime: 0,
          hasRootAccess: false,
          hasShellAccess: false,
          serverType: sourceServer?.type,
        },
      }))
    }
    
    // Test destination server
    try {
      const destResult = await testServerConnection(destServerId)
      setConnectionStatus(prev => ({
        ...prev,
        destination: {
          testing: false,
          success: destResult.success,
          message: destResult.message,
          responseTime: destResult.responseTimeMs,
          hasRootAccess: destResult.hasRootAccess,
          hasShellAccess: destResult.hasShellAccess,
          serverType: destServer?.type,
        },
      }))
    } catch (err) {
      setConnectionStatus(prev => ({
        ...prev,
        destination: {
          testing: false,
          success: false,
          message: 'Test failed - you can still continue',
          responseTime: 0,
          hasRootAccess: false,
          hasShellAccess: false,
          serverType: destServer?.type,
        },
      }))
    }
  }

  // Step 2 (combined): save servers, record connection test, save password
  // options, then either run the password-update terminal or go to Create.
  const handleServersContinue = async () => {
    if (!sourceServerId || !destServerId) return
    setLoading(true)
    try {
      await configureServers(createdJobId, sourceServerId, destServerId)
      // Test outcome is informational — never block on it.
      try { await testConnections(createdJobId) } catch (err) { console.error('Connection test:', err) }
      await setPasswordOptions(createdJobId, updateSourcePasswords, storePasswords)

      if (updateSourcePasswords) {
        // Show the password-update terminal inline before moving on.
        setPasswordUpdateStarted(true)
      } else {
        setCurrentStep(2)
        navigate(`/dashboard/migrations/${createdJobId}/create`, { replace: true })
      }
      setTimeout(saveState, 0)
    } catch (err) {
      console.error('Error continuing from servers step:', err)
      showAlert('Error', 'Error: ' + err.message, 'error')
    }
    setLoading(false)
  }

  // Handle password update completion
  const handlePasswordUpdateComplete = (data) => {
    setPasswordUpdateComplete(true)
    // Stay on this step - user clicks Continue button to proceed
  }

  // Step 5: Create Destination Emails
  const handleCreateEmails = async () => {
    setCreatingEmails(true)
    setEmailCreationStarted(true)
    setEmailCreationStatus(accounts.map(acc => ({
      email: acc.email,
      status: 'pending',
      message: 'Waiting to start...',
    })))
    
    try {
      // Call the actual backend API
      const response = await createDestinationEmails(createdJobId)
      
      // Update status based on API response
      if (response.results) {
        setEmailCreationStatus(response.results.map(result => ({
          email: result.email,
          status: result.success ? 'created' : 'failed',
          message: result.message || (result.success ? 'Created successfully' : 'Failed to create'),
        })))
      } else {
        // Fallback if API doesn't return per-account results
        setEmailCreationStatus(accounts.map(acc => ({
          email: acc.email,
          status: 'pending',
          message: 'API response received - processing...',
        })))
      }
    } catch (err) {
      console.error('Error creating emails:', err)
      // Mark all as failed if API call fails
      setEmailCreationStatus(accounts.map(acc => ({
        email: acc.email,
        status: 'failed',
        message: 'API error: ' + (err.message || 'Failed to connect to server'),
      })))
    }
    setCreatingEmails(false)
  }

  // Step 6: Start Migration
  const handleStartMigration = async () => {
    setMigrating(true)
    try {
      const progress = await getProgress(createdJobId)
      await startMigration(createdJobId, progress.config)
      setMigrationStatus('running')
      
      // Poll for progress
      const interval = setInterval(async () => {
        const updated = await getProgress(createdJobId)
        if (updated.status === 'COMPLETED' || updated.status === 'FAILED') {
          clearInterval(interval)
          setMigrationStatus(updated.status.toLowerCase())
        }
      }, 3000)
    } catch (err) {
      console.error('Error starting migration:', err)
      setMigrationStatus('failed')
    }
  }

  const getSelectedServer = (id) => servers.find(s => s.id === id)
  const getSelectedBatch = (id) => emailBatches.find(b => b.id === id)

  const canProceedToNext = () => {
    switch (currentStep) {
      case 0:
        if (inputMethod === 'batch') return !!selectedBatchId
        return !!manualInput.trim()
      case 1:
        // Servers + options: only require server selection (test is informational)
        return !!sourceServerId && !!destServerId
      case 2:
        // Create Emails: allow proceeding once every account is created/failed/skipped
        return emailCreationStatus.length > 0 && emailCreationStatus.every(s =>
          s.status === 'created' || s.status === 'failed' || s.status === 'success'
        )
      case 3:
        return migrationStatus !== 'running'
      default:
        return false
    }
  }

  const canNavigateToStep = (targetStep) => {
    // Can always go to previous steps or current step
    if (targetStep <= currentStep) return true
    // Can go to next step only if current step is complete
    // Special handling for each step's requirements
    switch (targetStep) {
      case 0: return true
      case 1: return !!createdJobId                                              // Servers & options
      case 2: return !!createdJobId && !!sourceServerId && !!destServerId        // Create emails
      case 3: return !!createdJobId && (emailCreationStarted || currentStep >= 2) // Migrate & resync
      default: return false
    }
  }

  const handleStepClick = (targetStep) => {
    if (!canNavigateToStep(targetStep)) return
    
    // Save current state before navigating
    saveState()
    
    // Update step and navigate
    setCurrentStep(targetStep)
    const stepId = STEPS[targetStep].id
    if (createdJobId) {
      navigate(`/dashboard/migrations/${createdJobId}/${stepId}`, { replace: true })
    }
  }

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center">
        {STEPS.map((step, index) => {
          const isClickable = canNavigateToStep(index)
          const isCompleted = index < currentStep
          const isCurrent = index === currentStep

          return (
            <div key={step.id} className="flex items-center flex-1 last:flex-none">
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => handleStepClick(index)}
                title={isClickable ? `Go to ${step.label}` : 'Complete earlier steps first'}
                className="flex flex-col items-center gap-1.5 group disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <div className={[
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200',
                  isCompleted ? 'bg-success text-white' : '',
                  isCurrent ? 'bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-1' : '',
                  index > currentStep ? 'bg-muted text-muted-foreground' : '',
                ].join(' ')}>
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : index + 1}
                </div>
                <span className={[
                  'text-[11px] leading-none hidden lg:block whitespace-nowrap',
                  isCurrent ? 'text-foreground font-medium' : 'text-muted-foreground',
                ].join(' ')}>
                  {step.label}
                </span>
              </button>

              {index < STEPS.length - 1 && (
                <div className={[
                  'flex-1 h-px mx-3 mb-4 transition-colors duration-300',
                  isCompleted ? 'bg-success' : 'bg-border',
                ].join(' ')} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEditMode ? jobName || 'Migration Setup' : 'New Migration'}
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">{STEPS[currentStep]?.description}</p>
      </div>

      {renderStepIndicator()}

      {/* Step 1: Upload Accounts */}
      {currentStep === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Email Accounts</CardTitle>
            <CardDescription>Add the email accounts you want to migrate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Migration Name</Label>
              <Input
                placeholder="e.g., Company Migration Q1 2024"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
              />
            </div>

            <div className="flex gap-2 p-1 bg-muted rounded-lg">
              <button
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  inputMethod === 'batch' ? 'bg-background shadow' : 'text-muted-foreground'
                }`}
                onClick={() => setInputMethod('batch')}
              >
                Email Batch
              </button>
              <button
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  inputMethod === 'manual' ? 'bg-background shadow' : 'text-muted-foreground'
                }`}
                onClick={() => setInputMethod('manual')}
              >
                Manual Entry
              </button>
            </div>

            {inputMethod === 'batch' ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Email Batch</Label>
                  <Popover open={batchOpen} onOpenChange={setBatchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={batchOpen}
                        className="w-full justify-between h-11"
                      >
                        {selectedBatchId
                          ? getSelectedBatch(selectedBatchId)?.name
                          : "Search and select a batch..."}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0">
                      <Command>
                        <CommandInput placeholder="Search batches..." />
                        <CommandEmpty>No email batches found.</CommandEmpty>
                        <CommandGroup>
                          {emailBatches.map((batch) => (
                            <CommandItem
                              key={batch.id}
                              value={batch.id}
                              onSelect={async (value) => {
                                setSelectedBatchId(value)
                                setBatchOpen(false)
                                // Fetch full batch details including items
                                try {
                                  const batchDetails = await getEmailBatch(value)
                                  setSelectedBatchDetails(batchDetails)
                                } catch (err) {
                                  console.error('Error loading batch details:', err)
                                }
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  selectedBatchId === batch.id ? 'opacity-100' : 'opacity-0'
                                }`}
                              />
                              <div className="flex flex-col">
                                <span>{batch.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {batch.itemCount} emails
                                  {batch.description && ` • ${batch.description}`}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                
                {selectedBatchId && (
                  <div className="p-3.5 bg-success/6 rounded-xl">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Check className="w-4 h-4 text-success" />
                      <span className="font-medium">{selectedBatchDetails?.name || getSelectedBatch(selectedBatchId)?.name}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {(() => {
                        const batch = selectedBatchDetails || getSelectedBatch(selectedBatchId)
                        if (!batch) return 'Loading...'
                        const selectedCount = batch.items?.filter(i => i.selected !== false).length || 0
                        const totalCount = batch.items?.length || 0
                        return `${selectedCount} of ${totalCount} emails selected for migration`
                      })()}
                    </div>
                  </div>
                )}
                
                {emailBatches.length === 0 && (
                  <div className="text-center py-6 rounded-xl bg-black/[0.03]">
                    <Users className="w-8 h-8 mx-auto text-primary/30 mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">No email batches available</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/dashboard/emails')}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Create Email Batch
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Email Accounts</Label>
                <textarea
                  className="w-full min-h-[200px] p-3 text-sm rounded-xl resize-none glass-input focus:outline-none focus:ring-2 focus:ring-ring/60"
                  placeholder={`user@example.com:your-password\nadmin@domain.com:your-password`}
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                />
              </div>
            )}

            <Button 
              className="w-full" 
              onClick={handleCreateJob}
              disabled={loading || !canProceedToNext()}
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Continue
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Servers & Options (servers + test + password options combined) */}
      {currentStep === 1 && !passwordUpdateStarted && (
        <Card>
          <CardHeader>
            <CardTitle>Servers & Options</CardTitle>
            <CardDescription>Choose servers, verify SSH access, and set password handling</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Source Server */}
            <div className="space-y-2">
              <Label>Source Server (cPanel)</Label>
              <Popover open={sourceOpen} onOpenChange={setSourceOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {sourceServerId ? (
                      <span className="flex items-center gap-2">
                        <Server className="w-4 h-4" />
                        {getSelectedServer(sourceServerId)?.name}
                      </span>
                    ) : (
                      "Select source server..."
                    )}
                    <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command>
                    <CommandInput placeholder="Search servers..." />
                    <CommandEmpty>No servers found.</CommandEmpty>
                    <CommandGroup>
                      {servers.filter(s => s.type === 'CPANEL').map((server) => (
                        <CommandItem
                          key={server.id}
                          value={server.id}
                          onSelect={(value) => {
                            setSourceServerId(value)
                            setSourceOpen(false)
                            // Reset connection status when server changes
                            setConnectionStatus(prev => ({
                              ...prev,
                              source: { testing: false, success: null, message: '', responseTime: 0, hasRootAccess: null }
                            }))
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${sourceServerId === server.id ? "opacity-100" : "opacity-0"}`} />
                          <div className="flex flex-col">
                            <span>{server.name}</span>
                            <span className="text-xs text-muted-foreground">{server.host}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
              
              {/* Source Server Connection Status */}
              {connectionStatus.source.success !== null && (
                <div className={`p-3 rounded-xl border-0 mt-2 ${connectionStatus.source.success ? 'bg-success/8' : 'bg-warning/8'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {connectionStatus.source.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                      )}
                      <span className={`text-sm font-medium ${connectionStatus.source.success ? 'text-success' : 'text-warning'}`}>
                        {connectionStatus.source.success ? 'Tested' : 'Test Issue'}
                      </span>
                      {connectionStatus.source.responseTime > 0 && (
                        <span className="text-xs text-muted-foreground">
                          ({connectionStatus.source.responseTime}ms)
                        </span>
                      )}
                    </div>
                    {/* Show appropriate access level based on server type */}
                    {connectionStatus.source.serverType === 'CPANEL' && connectionStatus.source.hasShellAccess !== null && (
                      <div className="flex items-center gap-1">
                        <Shield className={`w-4 h-4 ${connectionStatus.source.hasShellAccess ? 'text-success' : 'text-warning'}`} />
                        <span className={`text-xs ${connectionStatus.source.hasShellAccess ? 'text-success' : 'text-warning'}`}>
                          {connectionStatus.source.hasShellAccess ? 'Shell Access' : 'No Shell'}
                        </span>
                      </div>
                    )}
                    {connectionStatus.source.serverType === 'PLESK' && connectionStatus.source.hasRootAccess !== null && (
                      <div className="flex items-center gap-1">
                        <Shield className={`w-4 h-4 ${connectionStatus.source.hasRootAccess ? 'text-success' : 'text-warning'}`} />
                        <span className={`text-xs ${connectionStatus.source.hasRootAccess ? 'text-success' : 'text-warning'}`}>
                          {connectionStatus.source.hasRootAccess ? 'Root Access' : 'No Root'}
                        </span>
                      </div>
                    )}
                  </div>
                  {connectionStatus.source.message && (
                    <p className={`text-xs mt-1 ${connectionStatus.source.success ? 'text-success' : 'text-warning'}`}>
                      {connectionStatus.source.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Destination Server */}
            <div className="space-y-2">
              <Label>Destination Server (Plesk)</Label>
              <Popover open={destOpen} onOpenChange={setDestOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {destServerId ? (
                      <span className="flex items-center gap-2">
                        <Server className="w-4 h-4" />
                        {getSelectedServer(destServerId)?.name}
                      </span>
                    ) : (
                      "Select destination server..."
                    )}
                    <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command>
                    <CommandInput placeholder="Search servers..." />
                    <CommandEmpty>No servers found.</CommandEmpty>
                    <CommandGroup>
                      {servers.filter(s => s.type === 'PLESK').map((server) => (
                        <CommandItem
                          key={server.id}
                          value={server.id}
                          onSelect={(value) => {
                            setDestServerId(value)
                            setDestOpen(false)
                            // Reset connection status when server changes
                            setConnectionStatus(prev => ({
                              ...prev,
                              destination: { testing: false, success: null, message: '', responseTime: 0, hasRootAccess: null }
                            }))
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${destServerId === server.id ? "opacity-100" : "opacity-0"}`} />
                          <div className="flex flex-col">
                            <span>{server.name}</span>
                            <span className="text-xs text-muted-foreground">{server.host}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
              
              {/* Destination Server Connection Status */}
              {connectionStatus.destination.success !== null && (
                <div className={`p-3 rounded-xl border-0 mt-2 ${connectionStatus.destination.success ? 'bg-success/8' : 'bg-warning/8'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {connectionStatus.destination.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                      )}
                      <span className={`text-sm font-medium ${connectionStatus.destination.success ? 'text-success' : 'text-warning'}`}>
                        {connectionStatus.destination.success ? 'Tested' : 'Test Issue'}
                      </span>
                      {connectionStatus.destination.responseTime > 0 && (
                        <span className="text-xs text-muted-foreground">
                          ({connectionStatus.destination.responseTime}ms)
                        </span>
                      )}
                    </div>
                    {/* Show appropriate access level based on server type */}
                    {connectionStatus.destination.serverType === 'CPANEL' && connectionStatus.destination.hasShellAccess !== null && (
                      <div className="flex items-center gap-1">
                        <Shield className={`w-4 h-4 ${connectionStatus.destination.hasShellAccess ? 'text-success' : 'text-warning'}`} />
                        <span className={`text-xs ${connectionStatus.destination.hasShellAccess ? 'text-success' : 'text-warning'}`}>
                          {connectionStatus.destination.hasShellAccess ? 'Shell Access' : 'No Shell'}
                        </span>
                      </div>
                    )}
                    {connectionStatus.destination.serverType === 'PLESK' && connectionStatus.destination.hasRootAccess !== null && (
                      <div className="flex items-center gap-1">
                        <Shield className={`w-4 h-4 ${connectionStatus.destination.hasRootAccess ? 'text-success' : 'text-warning'}`} />
                        <span className={`text-xs ${connectionStatus.destination.hasRootAccess ? 'text-success' : 'text-warning'}`}>
                          {connectionStatus.destination.hasRootAccess ? 'Root Access' : 'No Root'}
                        </span>
                      </div>
                    )}
                  </div>
                  {connectionStatus.destination.message && (
                    <p className={`text-xs mt-1 ${connectionStatus.destination.success ? 'text-success' : 'text-warning'}`}>
                      {connectionStatus.destination.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Password Options (folded into this step) */}
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Password Options</span>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="update-source" className="font-medium">Update Source Passwords</Label>
                  <p className="text-sm text-muted-foreground">
                    Change passwords on the source server after migration
                  </p>
                </div>
                <Switch
                  id="update-source"
                  checked={updateSourcePasswords}
                  onCheckedChange={setUpdateSourcePasswords}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    <Label htmlFor="store-passwords" className="font-medium">Store Passwords</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Securely store email passwords for the migration process
                  </p>
                </div>
                <Switch
                  id="store-passwords"
                  checked={storePasswords}
                  onCheckedChange={setStorePasswords}
                />
              </div>
            </div>

            {/* Info message */}
            <div className="flex items-start gap-2 p-3 bg-accent border-0 rounded-lg">
              <Info className="w-4 h-4 text-accent-foreground/70 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-accent-foreground/80">
                Testing helps identify server capabilities. You can continue regardless of the test results —
                the migration will adapt based on available access levels.
              </p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                setCurrentStep(0)
                navigate(`/dashboard/migrations/${createdJobId}/accounts`, { replace: true })
              }}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                variant="secondary"
                onClick={testServerConnections}
                disabled={connectionStatus.source.testing || connectionStatus.destination.testing || !sourceServerId || !destServerId}
              >
                {(connectionStatus.source.testing || connectionStatus.destination.testing) ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCw className="w-4 h-4 mr-2" />
                )}
                {connectionStatus.source.success === null ? 'Test Connection' : 'Retest'}
              </Button>
              <Button
                className="flex-1"
                onClick={handleServersContinue}
                disabled={!canProceedToNext() || loading}
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {updateSourcePasswords ? 'Start Password Update' : 'Continue'}
                {!loading && !updateSourcePasswords && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2b: Password Update Terminal (shown when updateSourcePasswords is true) */}
      {currentStep === 1 && passwordUpdateStarted && (
        <PasswordUpdateWizard
          jobId={createdJobId}
          server={getSelectedServer(sourceServerId)}
          accounts={accounts}
          onComplete={handlePasswordUpdateComplete}
          onCancel={() => {
            setPasswordUpdateStarted(false)
            setPasswordUpdateComplete(false)
          }}
          onContinue={() => {
            setCurrentStep(2)
            navigate(`/dashboard/migrations/${createdJobId}/create`, { replace: true })
          }}
        />
      )}

      {/* Step 3: Create Destination Emails - With Real-Time Terminal */}
      {currentStep === 2 && (
        <EmailCreationWizard
          jobId={createdJobId}
          server={getSelectedServer(destServerId)}
          accounts={accounts}
          initialStatuses={creationInitialStatuses}
          onComplete={(result) => {
            // Map actual results to status
            const statuses = accounts.map(acc => {
              const resultItem = result?.results?.find(r => r.email === acc.email)
              // Handle skipped case
              if (result?.skipped) {
                return {
                  email: acc.email,
                  status: 'success',
                  message: 'Skipped - assumed already exists'
                }
              }
              return {
                email: acc.email,
                status: resultItem ? (resultItem.success ? 'created' : 'failed') : 'pending',
                message: resultItem?.message || (resultItem?.success ? 'Created successfully' : 'Failed to create')
              }
            })
            setEmailCreationStatus(statuses)
            setEmailCreationStarted(true)
            setCreatingEmails(false)
            saveState()
          }}
          onCancel={() => {
            setCreatingEmails(false)
            setCurrentStep(1)
            navigate(`/dashboard/migrations/${createdJobId}/servers`, { replace: true })
          }}
          onContinue={() => {
            setCurrentStep(3)  // Go to migration step
            setEmailCreationStarted(true)
            saveState()
            navigate(`/dashboard/migrations/${createdJobId}/migrate`, { replace: true })
          }}
        />
      )}

      {/* Step 4: Migrate & Resync */}
      {currentStep === 3 && (
        <>
          {/* Migration Summary Card */}
          <Card className="mb-4 card-soft">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">Migration Summary</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-muted/60 rounded-lg">
                  <span className="text-xs text-muted-foreground">Accounts</span>
                  <p className="font-semibold text-lg mt-0.5">{accounts.length}</p>
                </div>
                <div className="p-3 bg-muted/60 rounded-lg">
                  <span className="text-xs text-muted-foreground">Source</span>
                  <p className="font-medium mt-0.5 truncate">{getSelectedServer(sourceServerId)?.name || 'Not set'}</p>
                </div>
                <div className="p-3 bg-muted/60 rounded-lg">
                  <span className="text-xs text-muted-foreground">Destination</span>
                  <p className="font-medium mt-0.5 truncate">{getSelectedServer(destServerId)?.name || 'Not set'}</p>
                </div>
                <div className="p-3 bg-muted/60 rounded-lg">
                  <span className="text-xs text-muted-foreground">Password Sync</span>
                  <p className="font-medium mt-0.5">{updateSourcePasswords ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Migration Terminal — seeded with persisted progress so revisiting
              a migrated job shows results/percentages and enables resync. */}
          <MigrationTerminal
            jobId={createdJobId}
            accounts={accounts}
            initialProgress={accountProgress}
            onComplete={(result) => {
              setMigrationStatus(result.failed === 0 ? 'completed' : result.failed === result.total ? 'failed' : 'partial')
              saveState()
            }}
            onCancel={() => {
              setMigrationStatus(null)
            }}
          />

          {/* Back Button */}
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setCurrentStep(2)
                navigate(`/dashboard/migrations/${createdJobId}/create`, { replace: true })
              }}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button 
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/dashboard/migrations')}
            >
              View All Migrations
            </Button>
          </div>
        </>
      )}
      
      {/* Alert Modal */}
      <Dialog open={alertModal.isOpen} onOpenChange={(open) => setAlertModal({...alertModal, isOpen: open})}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                alertModal.type === 'success' ? 'bg-success/12' : 'bg-destructive/10'
              }`}>
                {alertModal.type === 'success' ? (
                  <CheckCircle className="w-4 h-4 text-success" />
                ) : (
                  <XCircle className="w-4 h-4 text-destructive" />
                )}
              </div>
              <DialogTitle className="text-base">{alertModal.title}</DialogTitle>
            </div>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-1">{alertModal.message}</p>
          <DialogFooter className="pt-2">
            <Button size="sm" onClick={() => setAlertModal({...alertModal, isOpen: false})}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
